import React, { useState, useEffect } from 'react';
import { X, FileText, Calendar as CalendarIcon, AlertCircle, Clock } from 'lucide-react';
import { useCalendarStore } from '../../../stores/calendarStore';
import { useContentPlanStore } from '../../../stores/contentPlanStore';
import { sanitizeHtml } from '../../../utils/sanitize';
import type { Platform } from '../../../types/calendar';
import type { ContentPlan } from '../../../stores/contentPlanStore';
import toast from 'react-hot-toast';

interface ImportPlanModalProps {
  plans: ContentPlan[];
  onClose: () => void;
}

interface ContentItem {
  title: string;
  description: string;
  platform: Platform;
  date: Date | null;
  dayOfWeek: string;
}

const DAYS_MAP: Record<string, number> = {
  'Segunda': 1,
  'Quarta': 3,
  'Sexta': 5,
  'Sábado': 6
};

// Get all available dates for a specific day of the week in the next 3 months
const getAvailableDates = (dayOfWeek: string): Date[] => {
  const dates: Date[] = [];
  const today = new Date();
  const threeMonthsFromNow = new Date();
  threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
  
  const targetDay = DAYS_MAP[dayOfWeek.split('-')[0]]; // Extract day name before hyphen
  if (typeof targetDay !== 'number') return dates;

  // Start from today
  const current = new Date(today);
  current.setHours(0, 0, 0, 0);

  // Find the first occurrence of the target day
  while (current.getDay() !== targetDay) {
    current.setDate(current.getDate() + 1);
  }

  // Add all occurrences within 3 months
  while (current <= threeMonthsFromNow) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 7); // Move to next week
  }

  return dates;
};

const getNextOccurrence = (dayOfWeek: string): Date => {
  const today = new Date();
  const targetDay = DAYS_MAP[dayOfWeek.split(':')[0].trim()]; // Extract day name before colon
  if (typeof targetDay !== 'number') {
    return today;
  }
  
  const daysUntilTarget = (targetDay + 7 - today.getDay()) % 7;
  const nextDate = new Date(today);
  nextDate.setDate(today.getDate() + daysUntilTarget);
  nextDate.setHours(9, 0, 0, 0);
  return nextDate;
};

export default function ImportPlanModal({ plans, onClose }: ImportPlanModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<ContentPlan | null>(null);
  const [items, setItems] = useState<ContentItem[]>([]);
  const { addEvent } = useCalendarStore();

  const handlePlanSelect = (plan: ContentPlan) => {
    setSelectedPlan(plan);
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitizeHtml(plan.content), 'text/html');
    const dayHeaders = doc.querySelectorAll('.day-header');
    
    const extractedItems = Array.from(dayHeaders).map(header => {
      const section = header.parentElement;
      const headerText = header.textContent || '';
      const dayOfWeek = headerText.split(':')[0].trim();
      const title = headerText.split(':').slice(1).join(':').trim();
      
      // Find the task content
      let description = '';
      let element = header.nextElementSibling;
      while (element && !element.classList.contains('day-header')) {
        if (element.classList.contains('task-label') && element.textContent?.includes('O que fazer')) {
          const contentElement = element.nextElementSibling;
          if (contentElement) {
            description = contentElement.textContent || '';
            break;
          }
        }
        element = element.nextElementSibling;
      }
      
      return {
        title,
        description,
        platform: 'instagram' as Platform,
        dayOfWeek,
        date: getNextOccurrence(headerText)
      };
    });

    setItems(extractedItems);
  };

  const handleDateChange = (index: number, dateString: string) => {
    try {
      if (!dateString) {
        setItems(prev => prev.map((item, i) => 
          i === index ? { ...item, date: null } : item
        ));
        return;
      }

      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        toast.error('Data inválida');
        return;
      }

      const item = items[index];
      const expectedDay = DAYS_MAP[item.dayOfWeek.split(':')[0].trim()];
      
      // Keep existing time if there is one
      const existingDate = item.date;
      const hours = existingDate ? existingDate.getHours() : 9;
      const minutes = existingDate ? existingDate.getMinutes() : 0;
      
      date.setHours(hours, minutes, 0, 0);
      
      setItems(prev => prev.map((item, i) => 
        i === index ? { ...item, date } : item
      ));
    } catch (error) {
      console.error('Error handling date change:', error);
      toast.error('Erro ao processar a data');
    }
  };

  const handleTimeChange = (index: number, timeString: string) => {
    try {
      const [hours, minutes] = timeString.split(':').map(Number);
      
      setItems(prev => prev.map((item, i) => {
        if (i !== index) return item;
        
        const newDate = item.date ? new Date(item.date) : new Date();
        newDate.setHours(hours, minutes, 0, 0);
        
        return { ...item, date: newDate };
      }));
    } catch (error) {
      console.error('Error handling time change:', error);
      toast.error('Erro ao processar o horário');
    }
  };

  const handlePlatformChange = (index: number, platform: Platform) => {
    setItems(prev => prev.map((item, i) => 
      i === index ? { ...item, platform } : item
    ));
  };

  const handleSave = () => {
    try {
      // Validate that all items have dates
      const allItemsHaveDates = items.every(item => item.date instanceof Date);
      if (!allItemsHaveDates) {
        toast.error('Por favor, selecione uma data e horário para todos os itens.');
        return;
      }

      // Validate that no two items are scheduled for the same date
      const usedDates = new Set<string>();
      for (const item of items) {
        if (!item.date) continue;
        const dateKey = item.date.toISOString().split('T')[0];
        if (usedDates.has(dateKey)) {
          toast.error('Não é possível agendar mais de um conteúdo para o mesmo dia.');
          return;
        }
        usedDates.add(dateKey);
      }

      // Add each item to the calendar
      items.forEach(item => {
        if (item.date) {
          const startDate = new Date(item.date);
          const endDate = new Date(startDate);
          endDate.setHours(endDate.getHours() + 1);

          addEvent({
            title: `${item.dayOfWeek}: ${item.title}`,
            start: startDate,
            end: endDate,
            platform: item.platform,
            priority: 'medium',
            status: 'draft',
            color: '#4F46E5',
            contentPlanId: selectedPlan?.id
          });
        }
      });

      toast.success('Plano adicionado ao calendário com sucesso!');
      onClose();
    } catch (error) {
      console.error('Error saving events:', error);
      toast.error('Erro ao salvar eventos no calendário');
    }
  };

  const formatDateForInput = (date: Date | null): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }
    return date.toISOString().split('T')[0];
  };

  const formatTimeForInput = (date: Date | null): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return '09:00';
    }
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white p-4 border-b border-gray-200 flex items-center justify-between z-10">
          <div className="flex items-center space-x-3">
            <CalendarIcon className="h-6 w-6 text-indigo-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Adicionar ao Calendário
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {!selectedPlan ? (
            <div className="space-y-4">
              <p className="text-gray-600 mb-4">
                Selecione um plano para importar:
              </p>
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  onClick={() => handlePlanSelect(plan)}
                  className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                >
                  <h3 className="font-medium text-gray-900">{plan.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Criado em {new Date(plan.createdAt).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">{selectedPlan.title}</h3>
                <button
                  onClick={() => setSelectedPlan(null)}
                  className="text-indigo-600 hover:text-indigo-700 text-sm"
                >
                  Escolher outro plano
                </button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-blue-800 font-medium">Datas Inteligentes</p>
                    <p className="text-blue-600 text-sm mt-1">
                      As datas serão automaticamente ajustadas para o dia da semana correto do conteúdo.
                      Por exemplo, conteúdo marcado para Segunda-feira só pode ser agendado em segundas.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {items.map((item, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="font-medium text-gray-900">{item.dayOfWeek}:</h3>
                      <p className="font-medium text-gray-900">{item.title}</p>
                    </div>
                    <p className="text-gray-600 text-sm mb-4">{item.description}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Data ({item.dayOfWeek.split(':')[0]})
                        </label>
                        <div className="relative">
                          <select
                            value={formatDateForInput(item.date)}
                            onChange={(e) => handleDateChange(index, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            required
                          >
                            <option value="">Selecione uma data</option>
                            {getAvailableDates(item.dayOfWeek).map((date) => (
                              <option key={date.toISOString()} value={formatDateForInput(date)}>
                                {date.toLocaleDateString('pt-BR')}
                              </option>
                            ))}
                          </select>
                          <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Horário
                        </label>
                        <div className="relative">
                          <input
                            type="time"
                            value={formatTimeForInput(item.date)}
                            onChange={(e) => handleTimeChange(index, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            required
                          />
                          <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Plataforma
                        </label>
                        <select
                          value={item.platform}
                          onChange={(e) => handlePlatformChange(index, e.target.value as Platform)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="instagram">Instagram</option>
                          <option value="youtube">YouTube</option>
                          <option value="tiktok">TikTok</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border-t p-4 bg-gray-50 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancelar
          </button>
          {selectedPlan && (
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Adicionar ao Calendário
            </button>
          )}
        </div>
      </div>
    </div>
  );
}